/**
 * DecisionDrawer — rechtes Panel fuer eine offene Entscheidung (v8).
 *
 * Ruhige Alternative zur „Buttonwand": ganze Zeile klickbar → Drawer mit
 * KPI-Chips · Optionen · Owner-Zuweisung · Begruendung · Historie · GENAU EINEM
 * orangen CTA „Entscheiden & zuweisen" (persistiert serverseitig + legt einen
 * Atlas-Task an, identisch zum DecisionBlock) und einem Link „Vollansicht
 * oeffnen" → /email-automation/decide/:cardId (Deep-Link bleibt funktionsfaehig).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink } from "lucide-react";
import { api } from "../api";
import { SideDrawer } from "../ui";
import { useToast } from "../ui/toast";
import { UserPicker, type Person } from "../components/UserPicker";
import { useDecisions, fmtInt } from "./EmailAutomationDecide";

export interface DrawerCard {
  id: string;
  kind: "quick_win" | "prereq" | "familie" | "automation";
  title: string;
  hj: number | null;      // freigeschaltete/gesparte h/Jahr
  hebelLabel: string;     // z.B. "SF-Flow"
  effort: string;         // "S" | "M" | "L"
  deepLink: string;       // Vollansicht-Route (/email-automation/decide/:id?…)
  options?: string[];     // Entscheidungs-Optionen (Default: Umsetzen/Zurückstellen/Ablehnen)
}

const DEFAULT_OPTIONS: [string, string][] = [
  ["Umsetzen", "Atlas-Task anlegen"],
  ["Zurückstellen", "später prüfen"],
  ["Ablehnen", "nicht verfolgen"],
];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("de-DE");
}

export function DecisionDrawer({ card, onClose }: { card: DrawerCard | null; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { decisions, reload } = useDecisions(true);

  const [option, setOption] = useState("");
  const [rationale, setRationale] = useState("");
  const [owner, setOwner] = useState<Person[]>([]);
  const [saving, setSaving] = useState(false);

  // Form beim Wechsel der Karte zuruecksetzen; Default-Option vorwaehlen.
  useEffect(() => {
    setOption(card?.options?.[0] ?? DEFAULT_OPTIONS[0][0]);
    setRationale("");
    setOwner([]);
  }, [card?.id]);

  const mine = card ? decisions.filter((d) => d.card_id === card.id) : [];
  const options: [string, string][] = card?.options?.length
    ? card.options.map((o) => [o, ""] as [string, string])
    : DEFAULT_OPTIONS;

  async function submit() {
    if (!card || saving) return;
    setSaving(true);
    try {
      await api.post("/email-automation/decisions", {
        card_id: card.id,
        card_kind: card.kind,
        card_title: card.title,
        question: null,
        chosen_option: option.trim() || options[0][0],
        rationale: rationale.trim() || null,
        assignee: owner[0]?.email ?? null,
        due_date: null,
      });
      await reload();
      toast.ok(
        "Entscheidung erfasst",
        owner[0]?.name ? `Atlas-Task angelegt · Owner ${owner[0].name}` : "Atlas-Task angelegt (dir selbst zugewiesen)",
      );
      onClose();
    } catch (e) {
      toast.err("Konnte nicht speichern", (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SideDrawer
      open={card != null}
      onClose={onClose}
      eyebrow="Offene Entscheidung"
      title={card?.title ?? ""}
      footer={
        <>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-control bg-action text-white px-4 py-2.5 text-sm font-semibold hover:brightness-95 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <Check className="w-4 h-4" /> {saving ? "Speichere …" : "Entscheiden & zuweisen"}
          </button>
          <button
            onClick={onClose}
            className="rounded-control border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            Schließen
          </button>
        </>
      }
    >
      {card && (
        <>
          {/* KPI-Chips: h/Jahr (Petrol) · Hebel · Aufwand */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-control border border-border bg-surface-2 px-2.5 py-2">
              <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-text-3">h/Jahr</div>
              <div className="font-numeric text-[0.95rem] font-semibold tabular-nums text-accent-700 dark:text-accent-300 mt-0.5">{card.hj != null ? `~${fmtInt(card.hj)}` : "—"}</div>
            </div>
            <div className="rounded-control border border-border bg-surface-2 px-2.5 py-2">
              <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-text-3">Hebel</div>
              <div className="text-[0.82rem] font-semibold text-text mt-0.5 truncate" title={card.hebelLabel}>{card.hebelLabel}</div>
            </div>
            <div className="rounded-control border border-border bg-surface-2 px-2.5 py-2">
              <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-text-3">Aufwand</div>
              <div className="text-[0.9rem] font-semibold text-text mt-0.5">{card.effort || "—"}</div>
            </div>
          </div>

          <button
            onClick={() => { onClose(); navigate(card.deepLink); }}
            className="mb-5 inline-flex items-center gap-1 text-[0.8rem] font-medium text-brand-600 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
          >
            Vollansicht öffnen <ExternalLink className="w-3.5 h-3.5" />
          </button>

          {/* Entscheidung — Optionen */}
          <div className="mb-5">
            <p className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mb-2">Entscheidung</p>
            <div className="flex flex-col gap-2">
              {options.map(([opt, desc]) => {
                const sel = option === opt;
                return (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 rounded-control border px-3 py-2.5 text-[0.86rem] cursor-pointer transition-colors ${
                      sel ? "border-accent-500 bg-accent-50 dark:bg-accent-500/10" : "border-border hover:border-border-strong"
                    }`}
                  >
                    <input
                      type="radio"
                      name="ea-decision"
                      checked={sel}
                      onChange={() => setOption(opt)}
                      className="w-4 h-4 accent-[rgb(var(--accent-500))]"
                    />
                    <span className="text-text">{opt}</span>
                    {desc && <span className="ml-auto text-[0.72rem] text-text-3">{desc}</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Owner zuweisen */}
          <div className="mb-5">
            <p className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mb-2">Owner zuweisen</p>
            <UserPicker selected={owner} onChange={setOwner} multiple={false} placeholder="Owner suchen (Name/Email) …" />
            <p className="mt-1.5 text-[0.72rem] text-text-3">Leer = dir selbst zuweisen.</p>
          </div>

          {/* Begründung */}
          <div className="mb-5">
            <p className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mb-2">Begründung</p>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Warum diese Entscheidung? (wird am Task + in der Historie festgehalten)"
              className="w-full min-h-[78px] rounded-control border border-border bg-surface px-3 py-2.5 text-[0.86rem] text-text placeholder:text-text-3 resize-y focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
            />
          </div>

          {/* Entscheidungs-Historie */}
          <div>
            <p className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mb-2">Entscheidungs-Historie</p>
            {mine.length === 0 ? (
              <div className="rounded-control border border-dashed border-border-strong bg-surface-2 px-3.5 py-3.5 text-center text-[0.8rem] text-text-3">
                Noch keine Entscheidung erfasst.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {mine.map((d) => (
                  <div key={d.id} className="rounded-control border border-border px-3 py-2">
                    <div className="text-[0.82rem] text-text">
                      <b className="font-semibold">{d.chosen_option || "Entschieden"}</b>
                    </div>
                    {d.rationale && <div className="text-[0.74rem] text-text-3 leading-snug mt-0.5">{d.rationale}</div>}
                    <div className="text-[0.7rem] text-text-3 mt-0.5">
                      {d.owner_name && <>Owner <b className="text-text-2">{d.owner_name}</b> · </>}
                      {d.decided_by} · {fmtDate(d.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </SideDrawer>
  );
}

export default DecisionDrawer;
